import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  PartnerPayoutDoc,
  PartnerPayoutHistoryEntry,
  PartnerPayoutReward,
  PartnerPayoutsView,
} from "../../shared/types";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import { PayoutMethodModal } from "../components/PayoutMethodModal";
import { useNative } from "../../shared/phone/media";
import { PayoutsNative } from "./PayoutsNative";

/**
 * The partner portal payouts view (node P5): the signed-in partner's OWN payout
 * position for the selected program. Every number is real, from GET
 * /api/partner/payouts, which reuses S1's money helpers (owed = cleared minus
 * recorded-paid). The partner VIEWS what is owed, clearing, and paid.
 *
 * MONEY RULE (load-bearing): there is NO pay action anywhere on this screen. The
 * program owner pays the partner directly; Flashadz records the payment and never
 * holds the money. Honest empty state; polished light + dark.
 */

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  CAD: "$",
  AUD: "$",
  EUR: "€",
  GBP: "£",
};

function money(cents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  const dollars = Math.abs(cents) / 100;
  const body = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${cents < 0 ? "-" : ""}${symbol}${body}`;
}

function money2(cents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  return `${symbol}${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** Parse a sqlite date/datetime as a local Date, or null. */
function toDate(s: string | null): Date | null {
  if (!s) return null;
  const day = s.slice(0, 10);
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-08-21" -> "Aug 21". */
function shortDate(s: string | null): string {
  const d = toDate(s);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}` : "";
}

/** "2026-08-21" -> "Aug 21, 2026". */
function longDate(s: string | null): string {
  const d = toDate(s);
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : "";
}

/** "Jul 1 to Jul 31, 2026" from a period pair. */
function periodLabel(start: string | null, end: string | null): string | null {
  const a = toDate(start);
  const b = toDate(end);
  if (!a || !b) return null;
  const left = `${MONTHS[a.getMonth()]} ${a.getDate()}`;
  const right = `${MONTHS[b.getMonth()]} ${b.getDate()}, ${b.getFullYear()}`;
  return `${left} to ${right}`;
}

function DURATION_LABEL(duration: PartnerPayoutReward["duration"]): string {
  if (duration === "recurring") return "Recurring";
  if (duration === "forever") return "Forever";
  return "One time";
}

/** The reward rate as a display string, from the real program config. */
function rewardRate(value: number, unit: PartnerPayoutReward["unit"], currency: string): string {
  if (unit === "percent") {
    const pct = value / 100;
    return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
  }
  return money(value, currency);
}

// ── How you earn card (real program reward config) ──────────────────────────

function EarnCard({ reward, currency }: { reward: PartnerPayoutReward; currency: string }) {
  const durationLabel = DURATION_LABEL(reward.duration);
  const basis = reward.duration === "recurring" ? "of every payment" : "per sale";

  if (reward.type === "ladder" && reward.tiers.length > 0) {
    let prev = 0;
    const bands = reward.tiers.map((t) => {
      const band = t.upToCount === null ? `${prev + 1}+ sales` : `${prev + 1} to ${t.upToCount}`;
      prev = t.upToCount ?? prev;
      return { band, rate: rewardRate(t.rewardCentsOrBps, reward.unit, currency), key: t.id };
    });
    return (
      <div className="card earncard">
        <div className="ec-head">
          <h3>How you earn</h3>
          <span className="ec-tag">Rising reward</span>
        </div>
        <div className="ec-tiers">
          {bands.map((b) => (
            <div className="ec-tier" key={b.key}>
              <span className="ec-band">{b.band}</span>
              <span className="ec-rate">{b.rate}</span>
            </div>
          ))}
        </div>
        <div className="ec-sub">
          {durationLabel} {basis}, held {reward.holdDays} days
        </div>
      </div>
    );
  }

  return (
    <div className="card earncard">
      <div className="ec-head">
        <h3>How you earn</h3>
      </div>
      <div className="ec-flat">
        {rewardRate(reward.valueBpsOrCents, reward.unit, currency)} <span>{basis}</span>
      </div>
      <div className="ec-sub">
        {durationLabel}, held {reward.holdDays} days
      </div>
    </div>
  );
}

// ── Documents before payout ──────────────────────────────────────────────────

const DOC_LABEL: Record<PartnerPayoutDoc["kind"], string> = {
  agreement: "Partner agreement",
  tax: "Tax form",
  id: "ID verification",
};

function docSub(doc: PartnerPayoutDoc): string {
  const need = doc.mandatory ? "mandatory" : "optional";
  if (doc.kind === "agreement") return `${doc.satisfied ? "signed" : "not signed"}, ${need}`;
  if (doc.kind === "tax") {
    const kind = doc.detail ? `${doc.detail} ` : "";
    return doc.satisfied ? `${kind}on file, ${need}` : `not on file, ${need}`;
  }
  return doc.satisfied ? `verified, ${need}` : need;
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12.5l4.2 4.2L19 7" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 7.5v.5" />
    </svg>
  );
}

// ── Payout detail drawer ─────────────────────────────────────────────────────

function PayoutDrawer({
  entry,
  productName,
  currency,
  onClose,
}: {
  entry: PartnerPayoutHistoryEntry;
  productName: string;
  currency: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const method =
    entry.methodLabel && entry.methodSnapshot
      ? [entry.methodLabel, entry.methodSnapshot.email ?? entry.methodSnapshot.account ?? null]
          .filter(Boolean)
          .join(" · ")
      : (entry.methodLabel ?? "Recorded by the owner");
  const period = periodLabel(entry.periodStart, entry.periodEnd);

  return (
    <div
      className="scrim open"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal wide" role="dialog" aria-modal="true" aria-label="Payout details">
        <div className="mhead">
          <h3>Payout {shortDate(entry.at) && `· ${shortDate(entry.at)}`}</h3>
          <button type="button" className="mx" aria-label="Close" onClick={onClose}>
            &#10005;
          </button>
        </div>
        <div className="mbody">
          <div className="pd-top">
            <div>
              <div className="pd-amt">{money2(entry.amountCents, currency)}</div>
              <div className="pd-status">Paid</div>
            </div>
            {entry.reference && (
              <div className="pd-ref">
                <div className="pd-reflbl">Reference</div>
                <div className="pd-refval">{entry.reference}</div>
              </div>
            )}
          </div>
          <div className="pd-grid">
            <div className="pd-item">
              <div className="pd-k">Date paid</div>
              <div className="pd-v">{longDate(entry.at) || "Recorded"}</div>
            </div>
            <div className="pd-item">
              <div className="pd-k">Method</div>
              <div className="pd-v">{method}</div>
            </div>
            <div className="pd-item">
              <div className="pd-k">Period</div>
              <div className="pd-v">{period ?? "Not set"}</div>
            </div>
            <div className="pd-item">
              <div className="pd-k">Product</div>
              <div className="pd-v">{productName}</div>
            </div>
          </div>
          {entry.note && (
            <div className="pd-note">
              <div className="pd-k">Note from {productName}</div>
              <div className="pd-noteval">{entry.note}</div>
            </div>
          )}
          {/* U0: a recorded payout stores a total, not the specific referrals it
              covered, so this describes the period rather than inventing a per-sale
              list (the amount would not match a period-window sum). See GRAPH-v2 s9. */}
          <div className="pd-covers">
            <div className="pd-k">What this covers</div>
            <p className="pd-coversval">
              {period
                ? `Your cleared rewards for ${period}.`
                : "Your cleared rewards for this run."}{" "}
              {productName} paid you directly and recorded it here. Flashadz never holds your money.
            </p>
          </div>
        </div>
        <div className="mfoot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── main ─────────────────────────────────────────────────────────────────────

export function PayoutsView() {
  const { currentMembership, applyMembership } = usePartnerAuth();
  const programId = currentMembership?.programId ?? null;

  const [view, setView] = useState<PartnerPayoutsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<PartnerPayoutHistoryEntry | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const reqRef = useRef(0);
  const native = useNative();

  const load = useCallback(async (pid: string) => {
    setError(null);
    const seq = ++reqRef.current;
    try {
      const data = await partnerApi.payouts(pid);
      if (reqRef.current === seq) setView(data);
    } catch (err) {
      if (reqRef.current !== seq) return;
      setView(null);
      setError(
        err instanceof PartnerApiError
          ? err.message
          : "We could not load your payouts. Try again in a moment.",
      );
    }
  }, []);

  useEffect(() => {
    if (!programId) return;
    setView(null);
    void load(programId);
  }, [programId, load]);

  const currency = view?.currency ?? "USD";
  const productName = view?.productName ?? currentMembership?.productName ?? "the program";

  const heroSub = useMemo(() => {
    if (!view) return "";
    const ready = view.readyCents;
    const clearing = view.nextClearingCents;
    if (view.position.state === "blocked" || view.position.state === "below_min") {
      return view.position.reason ?? "On hold";
    }
    if (ready > 0 && clearing > 0) {
      return `${money(ready, currency)} ready now, plus ${money(clearing, currency)} clearing ${shortDate(view.nextClearsAt)}`;
    }
    if (ready > 0) return `${money(ready, currency)} ready to pay`;
    if (clearing > 0) return `${money(clearing, currency)} clearing ${shortDate(view.nextClearsAt)}`;
    return "Nothing ready to pay yet";
  }, [view, currency]);

  if (native && programId) {
    return (
      <PayoutsNative
        view={view}
        error={error}
        onRetry={() => void load(programId)}
        programId={programId}
        onMethodSaved={(membership) => {
          applyMembership(membership);
          void load(programId);
        }}
      />
    );
  }

  if (error && !view) {
    return (
      <div className="content">
        <div className="es">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5M12 16.5v.5" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </div>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button type="button" className="btn ghost" onClick={() => programId && load(programId)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="content">
        <div className="home-loading">
          <div className="ps-dot" aria-hidden="true" />
          <span>Loading your payouts...</span>
        </div>
      </div>
    );
  }

  // Honest empty state: no earned reward and no recorded payout yet.
  if (!view.hasActivity) {
    return (
      <div className="content app-view">
        <div className="es">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="6" width="18" height="12" rx="2.5" />
              <circle cx="12" cy="12" r="2.6" />
              <path d="M6 9.5v5M18 9.5v5" />
            </svg>
          </div>
          <h3>No rewards yet</h3>
          <p>
            Once your referrals become customers, your rewards appear here and move from on hold to
            ready to pay. {productName} pays you directly, Flashadz records what you are owed.
          </p>
        </div>
      </div>
    );
  }

  const holdPct =
    view.holdElapsed !== null && view.holdTotal > 0
      ? Math.round((view.holdElapsed / view.holdTotal) * 100)
      : 100;
  const hasClearing = view.clearing.length > 0;
  // Blocked or below-minimum owed money is real and owed, just not payable yet, so
  // the hero shows it (with the reason) rather than a misleading $0.
  const gated = view.position.state === "blocked" || view.position.state === "below_min";
  const heroAmountCents = gated ? view.position.owedCents : view.nextTotalCents;

  return (
    <div className="content app-view">
      <div className="pay-grid">
        <div className="pay-main">
          <div className="nextpay">
            <div className="np-main">
              <div className="np-k">{gated ? "On hold" : "Next payout"}</div>
              <div className="np-amt">{money2(heroAmountCents, currency)}</div>
              <div className="np-sub">{heroSub}</div>
            </div>
            {hasClearing ? (
              <div className="np-side">
                <div className="np-when">Ready by</div>
                <div className="np-date">{longDate(view.nextClearsAt) || "Soon"}</div>
                <div className="np-bar">
                  <span style={{ width: `${holdPct}%` }} />
                </div>
                <div className="np-barlbl">
                  {view.holdElapsed} of {view.holdTotal} day hold
                </div>
              </div>
            ) : (
              <div className="np-side">
                <div className="np-when">Ready by</div>
                <div className="np-date">{view.readyCents > 0 ? "Now" : "-"}</div>
                <div className="np-bar">
                  <span style={{ width: view.readyCents > 0 ? "100%" : "0%" }} />
                </div>
                <div className="np-barlbl">
                  {view.readyCents > 0 ? "ready to pay" : gated ? "on hold" : "nothing clearing"}
                </div>
              </div>
            )}
          </div>

          <div className="card clearcard">
            <div className="card-h">
              <h3>On the way</h3>
              <span className="hint">{view.holdDays} day hold on each reward</span>
            </div>
            {hasClearing ? (
              <div className="clearlist">
                {view.clearing.map((c) => (
                  <div className="clr" key={c.referralId}>
                    <div className="clr-who">{c.personLabel ?? "New customer"}</div>
                    <div className="clr-amt">{money(c.amountCents, currency)}</div>
                    <div className="clr-meta">
                      sale {shortDate(c.soldAt)} <span>·</span> clears {shortDate(c.clearsAt)}
                    </div>
                    <div className="clr-when">
                      {c.daysRemaining <= 0 ? "ready" : `in ${c.daysRemaining}d`}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="clear-empty">
                Nothing clearing right now. New rewards land here for their {view.holdDays} day hold.
              </p>
            )}
          </div>

          <div className="card">
            <div className="card-h">
              <h3>Payment history</h3>
              <span className="hint">tap a payout for full details</span>
            </div>
            {view.history.length === 0 ? (
              <p className="clear-empty">No payments recorded yet.</p>
            ) : (
              <div className="rows">
                {view.history.map((h) =>
                  h.kind === "payout" ? (
                    <button
                      type="button"
                      className="row click"
                      key={h.id}
                      onClick={() => setDrawer(h)}
                    >
                      <span className="ic win">
                        <CheckIcon />
                      </span>
                      <div className="t">
                        <b>Payout {shortDate(h.at) && `· ${shortDate(h.at)}`}</b>
                        <div className="d">
                          {[h.methodLabel, h.reference].filter(Boolean).join(" · ") ||
                            "Recorded by the owner"}
                        </div>
                      </div>
                      <span className="amt g">{money(h.amountCents, currency)}</span>
                      <span className="row-chev" aria-hidden="true">
                        &#8250;
                      </span>
                    </button>
                  ) : (
                    <div className="row" key={h.id}>
                      <span className="ic hold">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M20 8a8 8 0 1 0 .5 5" />
                          <path d="M20 4v4h-4" />
                        </svg>
                      </span>
                      <div className="t">
                        <b>Clawback {shortDate(h.at) && `· ${shortDate(h.at)}`}</b>
                        <div className="d">
                          {h.personLabel ?? "A customer"} refunded, reward reversed
                        </div>
                      </div>
                      <span className="amt warn">{money(h.amountCents, currency)}</span>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        </div>

        <aside className="pay-rail">
          <EarnCard reward={view.reward} currency={currency} />

          <div className="card baltile-card">
            <div className="balrow">
              <span className="bk ready">Ready</span>
              <span className="bv">{money(view.readyCents, currency)}</span>
            </div>
            <div className="balrow">
              <span className="bk hold">On hold</span>
              <span className="bv">{money(view.onHoldCents, currency)}</span>
            </div>
            <div className="balrow">
              <span className="bk paid">Paid to date</span>
              <span className="bv">{money(view.paidToDateCents, currency)}</span>
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h3>Getting paid</h3>
            </div>
            {view.payoutMethod ? (
              <div className="pm">
                <div className="pm-info">
                  <div className="pm-a">{view.payoutMethodLabel ?? "Payout method"}</div>
                  {view.payoutMethodDetail && <div className="pm-b">{view.payoutMethodDetail}</div>}
                </div>
                <span className="pill saved">Saved</span>
              </div>
            ) : (
              <div className="pm pm-none">
                <div className="pm-info">
                  <div className="pm-a">No payout method yet</div>
                  <div className="pm-b">Add one so {productName} can pay you.</div>
                </div>
              </div>
            )}
            <button type="button" className="btn ghost sm gp-edit" onClick={() => setEditOpen(true)}>
              {view.payoutMethod ? "Edit payout method" : "Add payout method"}
            </button>
            <div className="gp-hint">
              {productName} pays you directly. Flashadz records what you are owed, it never holds your
              money.
            </div>

            <div className="gp-sep" />
            <div className="gp-sub">
              Documents <span>before payout</span>
            </div>
            <div className="rows">
              {view.documents.map((doc) => (
                <div className="row" key={doc.kind}>
                  <span className={`ic ${doc.satisfied ? "win" : "acc"}`}>
                    {doc.satisfied ? <CheckIcon /> : <InfoIcon />}
                  </span>
                  <div className="t">
                    <b>{DOC_LABEL[doc.kind]}</b>
                    <div className="d">{docSub(doc)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {drawer && (
        <PayoutDrawer
          entry={drawer}
          productName={productName}
          currency={currency}
          onClose={() => setDrawer(null)}
        />
      )}

      {editOpen && programId && (
        <PayoutMethodModal
          programId={programId}
          current={view.payoutMethod}
          onClose={() => setEditOpen(false)}
          onSaved={(membership) => {
            applyMembership(membership);
            setEditOpen(false);
            void load(programId);
          }}
        />
      )}
    </div>
  );
}
